// @flow
import { NullState } from 'bench-internal-theme';
import { ClientIdContext } from 'bk-web-app';
import { format, parse } from 'date-fns';
import { Query } from '@apollo/client/react/components';
import React from 'react';
import { WrappedSpinner } from 'spinner';

import compileFinancialPackageNotesData from './compileFinancialPackageNotesData';
import getFiscalYearsForDateRange from './getFiscalYearsForDateRange';
import {
  GET_BALANCE_SHEET_REPORT,
  GET_FISCAL_YEAR_END,
  GET_INCOME_STATEMENT_REPORT,
  GET_LEDGER_ACCOUNT_CATEGORY_ORDERS,
  GET_LEDGER_ACCOUNTS,
} from './queries';
import type { DateRange } from './types';
import FinancialPackageNotesView from './FinancialPackageNotesView';

type FinancialPackageNotesContainerProps = {
  dateRange: DateRange,
};

function FinancialPackageNotesContainer({ dateRange }: FinancialPackageNotesContainerProps) {
  const { startDate, endDate } = dateRange;
  return (
    <ClientIdContext.Consumer>
      {clientId => (
        <Query query={GET_FISCAL_YEAR_END} variables={{ clientId }}>
          {(getFiscalYearEnd: any) => {
            if (getFiscalYearEnd.error) {
              const errorMessage = getFiscalYearEnd.error && getFiscalYearEnd.error.message;
              return (
                <NullState
                  bigEmoji="🙈"
                  description={errorMessage}
                  title="Uh oh! The following error was encountered while fetching the data for this page."
                />
              );
            }
            if (getFiscalYearEnd.loading) {
              return <WrappedSpinner />;
            }
            const clientFiscalYearEnd = getFiscalYearEnd.data.client.fiscalYearEnd;
            const fiscalYears = getFiscalYearsForDateRange(dateRange, clientFiscalYearEnd);

            if (fiscalYears.length > 1) {
              return (
                <NullState
                  bigEmoji="📅"
                  description={`This client’s fiscal year end is ${format(
                    parse(clientFiscalYearEnd),
                    'MMM DD',
                  )}. Please select a date range within this client’s fiscal year.`}
                  title="The date range selected spans multiple fiscal years for this client."
                />
              );
            }
            const fiscalYear = fiscalYears[0];
            return (
              <Query query={GET_LEDGER_ACCOUNT_CATEGORY_ORDERS} variables={{ clientId }}>
                {(getLedgerAccountCategoryOrders: any) => (
                  <Query query={GET_BALANCE_SHEET_REPORT} variables={{ clientId, date: endDate }}>
                    {(getClosingBalanceSheetReport: any) => (
                      <Query
                        query={GET_INCOME_STATEMENT_REPORT}
                        variables={{ clientId, fromDate: startDate, toDate: endDate }}
                      >
                        {(getIncomeStatementReport: any) => (
                          <Query query={GET_LEDGER_ACCOUNTS} variables={{ clientId }}>
                            {(getLedgerAccounts: any) => {
                              if (
                                getLedgerAccountCategoryOrders.error ||
                                getClosingBalanceSheetReport.error ||
                                getIncomeStatementReport.error ||
                                getLedgerAccounts.error
                              ) {
                                const errorMessage = [
                                  getLedgerAccountCategoryOrders.error &&
                                    getLedgerAccountCategoryOrders.error.message,
                                  getLedgerAccounts.error && getLedgerAccounts.error.message,
                                  getClosingBalanceSheetReport.error &&
                                    getClosingBalanceSheetReport.error.message,
                                  getIncomeStatementReport.error &&
                                    getIncomeStatementReport.error.message,
                                ].join('\n');
                                return (
                                  <NullState
                                    bigEmoji="🙈"
                                    description={errorMessage}
                                    title="Uh oh! The following error was encountered while fetching the data for this page."
                                  />
                                );
                              }
                              if (
                                getLedgerAccountCategoryOrders.loading ||
                                getClosingBalanceSheetReport.loading ||
                                getIncomeStatementReport.loading ||
                                getLedgerAccountCategoryOrders.loading
                              ) {
                                return <WrappedSpinner />;
                              }
                              // Ensure no data is undefined before processing it further
                              const ledgerAccountCategoryOrders =
                                getLedgerAccountCategoryOrders.data.ledgerAccountCategoryOrders ||
                                [];
                              const ledgerAccounts = getLedgerAccounts?.data?.ledgerAccounts || [];
                              const closingBalanceSheetReport =
                                getClosingBalanceSheetReport.data.balanceSheetReport || {};
                              const closingBalanceSheetLedgerSummaries =
                                closingBalanceSheetReport.balanceSheetLedgerSummaries || [];
                              const closingBalanceSheetCategorySummaries =
                                closingBalanceSheetReport.balanceSheetCategorySummaries || [];
                              const incomeStatementReport =
                                getIncomeStatementReport.data.incomeStatementReport || {};
                              const incomeStatementLedgerSummaries =
                                incomeStatementReport.incomeStatementLedgerSummaries || [];
                              const incomeStatementCategorySummaries =
                                incomeStatementReport.incomeStatementCategorySummaries || [];
                              const twoDigitFiscalYear = fiscalYear.toString().slice(-2);
                              const hashtagFiscalYear = `#y${twoDigitFiscalYear}`;
                              const hashtagPeerReviewTag = `#p${twoDigitFiscalYear}`;
                              const hashtagResolvedTag = `#rs${twoDigitFiscalYear}`;
                              const { ledgerAccountCategories, ledgerAccountCategoryTotalsMap } =
                                compileFinancialPackageNotesData(
                                  closingBalanceSheetCategorySummaries,
                                  closingBalanceSheetLedgerSummaries,
                                  hashtagFiscalYear,
                                  hashtagPeerReviewTag,
                                  hashtagResolvedTag,
                                  incomeStatementCategorySummaries,
                                  incomeStatementLedgerSummaries,
                                  ledgerAccounts,
                                  ledgerAccountCategoryOrders,
                                );
                              return (
                                <FinancialPackageNotesView
                                  endDate={endDate}
                                  fiscalYear={fiscalYear}
                                  hashtagFiscalYearTag={hashtagFiscalYear}
                                  hashtagPeerReviewTag={hashtagPeerReviewTag}
                                  hashtagResolvedTag={hashtagResolvedTag}
                                  ledgerAccountCategories={ledgerAccountCategories}
                                  ledgerAccountCategoryTotalsMap={ledgerAccountCategoryTotalsMap}
                                  startDate={startDate}
                                />
                              );
                            }}
                          </Query>
                        )}
                      </Query>
                    )}
                  </Query>
                )}
              </Query>
            );
          }}
        </Query>
      )}
    </ClientIdContext.Consumer>
  );
}
export default FinancialPackageNotesContainer;
